import React from "react";
import { Box, Typography, Paper, Tabs, Tab } from "@mui/material";
import {
  Public,
  Store as StoreIcon,
  LocalOffer,
  EventAvailable,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { gradients, boxShadows, transparentColors } from "../../utils/theme";

interface ExploreTabsProps {
  activeTab: "malls" | "stores" | "offers" | "events";
  onTabChange: (
    event: React.SyntheticEvent,
    value: "malls" | "stores" | "offers" | "events",
  ) => void;
  mallsCount?: number;
  storesCount?: number;
  offersCount?: number;
  eventsCount?: number;
}

/**
 * Explore Tabs Component - MallInHand
 *
 * Modern tab navigation with navy and gold accents for shopping discovery
 */
const ExploreTabs: React.FC<ExploreTabsProps> = ({
  activeTab,
  onTabChange,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          mb: 4,
          background: "white",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: boxShadows.card,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={onTabChange}
          variant="fullWidth"
          sx={{
            minHeight: { xs: 60, md: 80 },
            bgcolor: "background.paper",
            "& .MuiTabs-indicator": {
              height: 4,
              background: gradients.navyLux,
              borderRadius: "4px 4px 0 0",
              boxShadow: boxShadows.primary,
            },
            "& .MuiTab-root": {
              py: { xs: 2.5, md: 3.5 },
              px: { xs: 2, md: 3 },
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              fontWeight: 600,
              textTransform: "none",
              color: "text.secondary",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              borderBottom: "3px solid transparent",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: transparentColors.black05,
                opacity: 0,
                transition: "opacity 0.3s ease",
                zIndex: -1,
              },
              "&:hover": {
                color: "primary.main",
                transform: "translateY(-2px)",
                "&::after": {
                  opacity: 1,
                },
              },
              "&.Mui-selected": {
                color: "primary.main",
                fontWeight: 700,
                background: `linear-gradient(to bottom, ${transparentColors.white10}, transparent)`,
                "&::after": {
                  opacity: 0,
                },
              },
            },
            "& .MuiTab-iconWrapper": {
              fontSize: { xs: 22, md: 26 },
              mb: 0.75,
              transition: "transform 0.2s ease",
            },
            "& .MuiTab-root:hover .MuiTab-iconWrapper": {
              transform: "scale(1.1)",
            },
          }}
        >
          <Tab
            value="malls"
            label={
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body1"
                  fontWeight="inherit"
                  sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                >
                  Browse Malls
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.75,
                    display: { xs: "none", sm: "block" },
                    fontSize: "0.75rem",
                  }}
                >
                  All locations
                </Typography>
              </Box>
            }
            icon={<Public />}
            iconPosition="start"
          />
          <Tab
            value="stores"
            label={
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body1"
                  fontWeight="inherit"
                  sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                >
                  Find Stores
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.75,
                    display: { xs: "none", sm: "block" },
                    fontSize: "0.75rem",
                  }}
                >
                  Top brands
                </Typography>
              </Box>
            }
            icon={<StoreIcon />}
            iconPosition="start"
          />
          <Tab
            value="offers"
            label={
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body1"
                  fontWeight="inherit"
                  sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                >
                  Explore Offers
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.75,
                    display: { xs: "none", sm: "block" },
                    fontSize: "0.75rem",
                  }}
                >
                  Best deals
                </Typography>
              </Box>
            }
            icon={<LocalOffer />}
            iconPosition="start"
          />
          <Tab
            value="events"
            label={
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body1"
                  fontWeight="inherit"
                  sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                >
                  Explore Events
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.75,
                    display: { xs: "none", sm: "block" },
                    fontSize: "0.75rem",
                  }}
                >
                  Live experiences
                </Typography>
              </Box>
            }
            icon={<EventAvailable />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>
    </motion.div>
  );
};

export default ExploreTabs;
